import { useState } from 'react'
import { Button } from '../ui'
import { useTranslation } from 'react-i18next'
import { Sparkles, Trophy, User, ArrowRight, CheckCircle2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { soundFx } from '../../lib/soundEffects'

import { useTenant } from '../../contexts/TenantContext' // Import context

export const BracketGenerator = ({ tournamentId, teams, onGenerated }) => {
    const { activeTenant } = useTenant() // Get tenant info
    const { t } = useTranslation()
    const [generating, setGenerating] = useState(false)
    const [success, setSuccess] = useState(false)

    const isValidCount = [2, 4, 8, 16, 32, 64, 128].includes(teams.length)
    const isPro = activeTenant?.plan === 'pro'
    // Limit: Free users max 4 teams (Semi Finals), Pro unlimited
    const isLocked = !isPro && teams.length > 4

    const generateBracket = async (empty = false) => {
        if (teams.length < 2) {
            soundFx.play('error')
            alert(t('common.min_teams_bracket'))
            return
        }

        if (!confirm(empty ? 'Wil je een compleet leeg schema genereren om alles zelf in te vullen?' : t('common.confirm_generate_bracket', { count: teams.length }))) return

        setGenerating(true)
        soundFx.play('click')

        try {
            // 1. Determine size (4, 8, 16, 32, 64)
            const count = teams.length
            let size = 2
            while (size < count) size *= 2

            console.log(`Generating bracket for ${count} teams (Schedule Size: ${size})`)

            const { error: deleteError } = await supabase
                .from('matches')
                .delete()
                .eq('tournament_id', tournamentId)
                .eq('type', 'knockout')

            // 3. Shuffle Teams (skip if empty)
            const shuffled = empty ? [] : [...teams].sort(() => Math.random() - 0.5)

            const matchesToInsert = []

            const getRoundName = (roundMatchCount) => {
                if (roundMatchCount === 1) return t('bracket_page.finale')
                if (roundMatchCount === 2) return t('bracket_page.halve_finale')
                if (roundMatchCount === 4) return t('bracket_page.kwartfinale')
                if (roundMatchCount === 8) return t('bracket_page.achtste_finale')
                if (roundMatchCount === 16) return t('bracket_page.zestiende_finale')
                return t('bracket_page.ronde_van', { count: roundMatchCount * 2 })
            }

            // 1. Generate Round 1 matches
            const firstRoundMatches = size / 2
            for (let i = 0; i < firstRoundMatches; i++) {
                matchesToInsert.push({
                    tournament_id: tournamentId,
                    type: 'knockout',
                    round_name: getRoundName(firstRoundMatches),
                    home_team_id: empty ? null : (shuffled[i * 2]?.id ?? null),
                    away_team_id: empty ? null : (shuffled[i * 2 + 1]?.id ?? null),
                    round_index: 0,
                    match_index: i,
                    next_match_index: firstRoundMatches === 1 ? null : Math.floor(i / 2)
                })
            }

            // 2. Generate Subsequent Rounds
            let currentRoundMatches = firstRoundMatches / 2
            let roundIndex = 1

            while (currentRoundMatches >= 1) {
                for (let i = 0; i < currentRoundMatches; i++) {
                    matchesToInsert.push({
                        tournament_id: tournamentId,
                        type: 'knockout',
                        round_name: getRoundName(currentRoundMatches),
                        home_team_id: null,
                        away_team_id: null,
                        round_index: roundIndex,
                        match_index: i,
                        next_match_index: currentRoundMatches === 1 ? null : Math.floor(i / 2)
                    })
                }
                currentRoundMatches /= 2
                roundIndex++
            }

            const { error } = await supabase.from('matches').insert(matchesToInsert)
            if (error) throw error

            setSuccess(true)
            soundFx.play('success')

            setTimeout(() => {
                onGenerated()
            }, 1500)

        } catch (err) {
            console.error(err)
            soundFx.play('error')
            alert(t('common.gen_error') + err.message)
            setGenerating(false)
        }
    }

    if (success) {
        return (
            <div className="text-center p-12 bg-green-50 rounded-xl border-2 border-green-200 animate-fade-in">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="text-green-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">{t('bracket_generator.success_title')}</h3>
                <p className="text-green-600">{t('bracket_generator.success_subtitle')}</p>
            </div>
        )
    }

    return (
        <div className="text-center p-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
            <Trophy className="mx-auto text-yellow-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-gray-800 mb-2">{t('bracket_generator.title')}</h3>
            
            {!isValidCount && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-6">
                    <p className="text-orange-800 font-bold mb-1">⚠️ Afwijkend aantal teams ({teams.length})</p>
                    <p className="text-orange-700 text-sm mb-2">
                        Voor een perfect schema heb je exact 2, 4, 8, of 16 teams nodig. Het systeem zal nu automatisch een groter schema maken met enkele "Lege" plekken.
                    </p>
                </div>
            )}

            <p className="text-gray-500 mb-6">
                {t('bracket_generator.description', { count: teams.length })}<br />
                {t('bracket_generator.shuffle_hint')}
            </p>

            {isLocked ? (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl mb-4">
                    <h4 className="font-bold text-yellow-800 flex items-center justify-center gap-2 mb-2">
                        <Trophy size={16} /> {t('bracket_generator.upgrade_needed')}
                    </h4>
                    <p className="text-sm text-yellow-700 mb-4">
                        {t('bracket_generator.upgrade_description', { count: teams.length })}
                    </p>
                    <Button
                        onClick={() => window.location.href = '/admin/upgrade'}
                        className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white w-full shadow-md"
                    >
                        {t('bracket_generator.upgrade_btn')}
                    </Button>
                </div>
            ) : (
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Button onClick={() => generateBracket(false)} disabled={generating || teams.length < 2} className="bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg hover:scale-105 transition-transform w-full sm:w-auto disabled:opacity-50 disabled:hover:scale-100">
                        {generating ? <><Sparkles size={20} className="mr-2 animate-spin" /> {t('bracket_generator.generating_status')}</> : <><Sparkles size={20} className="mr-2" /> Automatisch Indelen</>}
                    </Button>
                    <Button onClick={() => generateBracket(true)} disabled={generating || teams.length < 2} variant="secondary" className="w-full sm:w-auto shadow-md disabled:opacity-50">
                        <User size={18} className="mr-2" /> Maak Leeg Schema (Handmatig)
                    </Button>
                </div>
            )}
        </div>
    )
}
